import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Form, Table, Badge } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const ACCOUNT_LEVELS = {
  testing: 1000,
  standard: 50000,
  professional: 125000,
  enterprise: 300000
};

const TenantTokens = ({ auth }) => {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  const [newAllocation, setNewAllocation] = useState('');
  const [allocationReason, setAllocationReason] = useState('');
  const [allocationLoading, setAllocationLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tenant details
      const tenantResponse = await fetch(`/api/admin/tenants/${id}`, { credentials: 'include' });
      if (tenantResponse.ok) {
        const data = await tenantResponse.json();
        setTenant(data.tenant);
        setNewAllocation(data.tenant.monthly_token_allocation);
      }

      // Fetch token history
      const historyResponse = await fetch(`/api/admin/tenants/${id}/token-history`, { credentials: 'include' });
      if (historyResponse.ok) {
        const data = await historyResponse.json();
        setHistory(data.adjustments || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantTokens = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const amount = parseInt(grantAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid token amount');
      return;
    }

    try {
      setGrantLoading(true);

      const response = await fetch(`/api/admin/tenants/${id}/grant-tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tokens: amount,
          reason: grantReason || 'One-off token grant'
        })
      });

      if (response.ok) {
        setSuccess(`Successfully granted ${formatNumber(amount)} tokens`);
        setGrantAmount('');
        setGrantReason('');
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to grant tokens');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleChangeAllocation = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const amount = parseInt(newAllocation);
    if (!amount || amount <= 0) {
      setError('Please enter a valid allocation amount');
      return;
    }

    try {
      setAllocationLoading(true);

      const response = await fetch(`/api/admin/tenants/${id}/allocation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          monthly_allocation: amount,
          reason: allocationReason || 'Monthly allocation change'
        })
      });

      if (response.ok) {
        setSuccess(`Monthly allocation changed to ${formatNumber(amount)} tokens`);
        setAllocationReason('');
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to change allocation');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAllocationLoading(false);
    }
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <TenantManagementLayout title="Token Management">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading token data...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  if (!tenant) {
    return (
      <TenantManagementLayout title="Token Management">
        <Alert variant="warning">Tenant not found</Alert>
      </TenantManagementLayout>
    );
  }

  // Use actual tracked monthly usage from API
  const tokensUsed = tenant.monthly_tokens_used || 0;
  // Total capacity = current balance + what was used (to get back to starting point)
  const totalCapacity = tenant.token_balance + tokensUsed;
  const usagePercent = totalCapacity > 0
    ? Math.round((tokensUsed / totalCapacity) * 100)
    : 0;

  return (
    <TenantManagementLayout
      title={`Token Management - ${tenant.name}`}
      subtitle="Manage token allocations and grants"
    >
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Token Overview */}
      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ background: '#fee2e2', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#dc2626' }}>
                {formatNumber(tokensUsed)}
              </div>
              <div style={{ color: '#dc2626', fontSize: '0.9rem' }}>Used This Month</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#f0fdf4', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#166534' }}>
                {formatNumber(tenant.token_balance)}
              </div>
              <div style={{ color: '#166534', fontSize: '0.9rem' }}>Total Remaining</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#fef3c7', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#92400e' }}>
                {formatNumber(tenant.oneoff_token_balance)}
              </div>
              <div style={{ color: '#92400e', fontSize: '0.9rem' }}>Bonus Remaining</div>
              <div style={{ color: '#b45309', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                (consumed first)
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#eff6ff', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                {formatNumber(tenant.monthly_token_allocation)}
              </div>
              <div style={{ color: '#1e40af', fontSize: '0.9rem' }}>Monthly Allocation</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usage Progress Bar */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <span>Token Usage This Month</span>
            <span>
              {formatNumber(tokensUsed)} / {formatNumber(totalCapacity)} tokens
            </span>
          </div>
          <div style={{
            height: '12px',
            background: '#e2e8f0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min(usagePercent, 100)}%`,
              background: usagePercent > 90 ? '#ef4444' : usagePercent > 70 ? '#f59e0b' : '#22c55e',
              transition: 'width 0.3s'
            }} />
          </div>
          <div className="mt-3" style={{ fontSize: '0.9rem' }}>
            <strong>Breakdown:</strong>
            <div className="d-flex gap-4 mt-1">
              <span>
                <span style={{ color: '#92400e' }}>●</span> Bonus: {formatNumber(tenant.oneoff_token_balance)} remaining
                <span className="text-muted"> (consumed first)</span>
              </span>
              <span>
                <span style={{ color: '#1e40af' }}>●</span> Monthly: {formatNumber(tenant.token_balance - tenant.oneoff_token_balance)} / {formatNumber(tenant.monthly_token_allocation)}
              </span>
            </div>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
            Account level: <Badge bg="primary">{tenant.account_level}</Badge>
            {' '}(Default: {formatNumber(ACCOUNT_LEVELS[tenant.account_level])} tokens/month)
          </div>
        </Card.Body>
      </Card>

      <Row>
        {/* Grant One-Off Tokens */}
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Grant One-Off Tokens</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                One-off tokens do NOT reset monthly. They are consumed first before monthly allocation.
              </p>
              <Form onSubmit={handleGrantTokens}>
                <Form.Group className="mb-3">
                  <Form.Label>Token Amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    placeholder="10000"
                    required
                  />
                  <Form.Text className="text-muted">
                    1,000 tokens = $1.00
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reason (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    placeholder="Promotional grant, support compensation, etc."
                  />
                </Form.Group>
                <Button
                  variant="success"
                  type="submit"
                  disabled={grantLoading}
                >
                  {grantLoading ? 'Granting...' : 'Grant Tokens'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Change Monthly Allocation */}
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Change Monthly Allocation</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                Changes the monthly token allocation. This persists across monthly resets.
              </p>
              <Form onSubmit={handleChangeAllocation}>
                <Form.Group className="mb-3">
                  <Form.Label>New Monthly Allocation</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={newAllocation}
                    onChange={(e) => setNewAllocation(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Current: {formatNumber(tenant.monthly_token_allocation)} | Default for {tenant.account_level}: {formatNumber(ACCOUNT_LEVELS[tenant.account_level])}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reason (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={allocationReason}
                    onChange={(e) => setAllocationReason(e.target.value)}
                    placeholder="Special arrangement, pilot program, etc."
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={allocationLoading}
                >
                  {allocationLoading ? 'Updating...' : 'Update Allocation'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Token History */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Token Adjustment History</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={{ padding: '1rem' }}>Date</th>
                <th style={{ padding: '1rem' }}>Type</th>
                <th style={{ padding: '1rem' }}>Amount</th>
                <th style={{ padding: '1rem' }}>By</th>
                <th style={{ padding: '1rem' }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No token adjustments yet
                  </td>
                </tr>
              ) : (
                history.map((adj, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '1rem' }}>{formatDate(adj.created_at)}</td>
                    <td style={{ padding: '1rem' }}>
                      <Badge bg={adj.adjustment_type === 'oneoff_grant' ? 'success' : 'info'}>
                        {adj.adjustment_type === 'oneoff_grant' ? 'Grant' : 'Allocation Change'}
                      </Badge>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {adj.adjustment_type === 'oneoff_grant' ? (
                        <span style={{ color: '#16a34a', fontWeight: '600' }}>
                          +{formatNumber(adj.amount)}
                        </span>
                      ) : (
                        <span>
                          {formatNumber(adj.previous_value)} → {formatNumber(adj.amount)}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {adj.adjustedByUser?.first_name
                        ? `${adj.adjustedByUser.first_name} ${adj.adjustedByUser.last_name || ''}`.trim()
                        : adj.adjustedByUser?.email || 'Unknown'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {adj.reason || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <div className="mt-4">
        <Link to={`/admin/tenants/${id}`} className="btn btn-outline-secondary">
          Back to Tenant Details
        </Link>
      </div>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantTokens);
